import React, { useEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import {
  arrow,
  autoUpdate,
  FloatingArrow,
  useClick,
  useDismiss,
  useFloating,
  shift,
  offset,
  useInteractions,
} from '@floating-ui/react'
import { Button } from '@demandlocal/components'
import { ReactComponent as ChevronUp } from './../../images/icons/chevron_up.svg'
import { ReactComponent as ChevronDown } from './../../images/icons/chevron_down.svg'
import { ReactComponent as SortUp } from './../../images/icons/sort_up.svg'
import { ReactComponent as SortDown } from './../../images/icons/sort_down.svg'
import { HtmlPortalNode } from 'react-reverse-portal'
import * as portals from 'react-reverse-portal'
import classNames from 'classnames'

type Props = {
  sorting?: Record<string, unknown>
  portalNode: HtmlPortalNode
  choices: Array<{ label: string; key: string }>
  setSorting: React.Dispatch<React.SetStateAction<Record<string, unknown>>>
}

const SortingController = (props: Props): JSX.Element => {
  const { sorting = {}, portalNode, choices, setSorting } = props
  const sortKey = Object.keys(sorting)?.[0] ?? null
  const sortOrder = (Object.values(sorting)?.[0]?.toString() as 'asc' | 'desc') ?? 'asc'

  const [sortSelected, setSortSelected] = useState<string | null>(sortKey)
  const [expanded, setExpanded] = useState<boolean>(false)
  const [order, setOrder] = useState<'asc' | 'desc'>(sortOrder)

  const isSorting = useMemo(() => sortSelected !== null, [sortSelected])

  const resetValues = (): void => {
    setOrder('asc')
    setSorting({})
    setSortSelected(null)
  }

  useEffect(() => {
    if (sortSelected) setSorting({ [sortSelected]: order })
    else setSorting({})
  }, [order, sortSelected, setSorting])

  const arrowRef = useRef(null)

  const { context, strategy, refs } = useFloating({
    placement: 'bottom-end',
    middleware: [
      shift(),
      arrow({ element: arrowRef }),
      offset({ mainAxis: 0, crossAxis: 0 }),
    ],
    open: expanded,
    whileElementsMounted: autoUpdate,
    onOpenChange: setExpanded,
  })

  const dismiss = useDismiss(context)
  const click = useClick(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss])

  return (
    <>
      <portals.InPortal node={portalNode}>
        <div className={'flex flex-row gap-3'}>
          <div ref={refs.setReference} {...getReferenceProps()}>
            <Button
              animate={false}
              onClick={(e) => {
                e.preventDefault()
              }}
              variant="tertiary"
              rightSlot={
                <motion.div className={'flex items-center justify-center gap-2'}>
                  {expanded ? <ChevronUp fill="black" /> : <ChevronDown fill="black" />}
                </motion.div>
              }>
              <span className={'text-primary-xiketic'}>Sort by</span>
            </Button>
          </div>
          <Button
            animate={false}
            key={'sorting'}
            variant="tertiary"
            leftSlot={
              <motion.div className={'flex items-center justify-center gap-2'}>
                <SortUp
                  onClick={(e) => {
                    e.preventDefault()
                    setOrder('asc')
                  }}
                  className={classNames('', { 'opacity-40': order !== 'asc' })}
                />
              </motion.div>
            }
            rightSlot={
              <motion.div className={'flex items-center justify-center gap-2'}>
                <SortDown
                  onClick={(e) => {
                    e.preventDefault()
                    setOrder('desc')
                  }}
                  className={classNames('', { 'opacity-40': order !== 'desc' })}
                />
              </motion.div>
            }>
            <></>
          </Button>
          {isSorting && (
            <motion.div>
              <Button
                animate={false}
                variant={'tertiary'}
                onClick={(e) => {
                  e.preventDefault()
                  resetValues()
                  // setExpanded(false)
                }}>
                <span className={'text-primary-xiketic'}>Clear sort</span>
              </Button>
            </motion.div>
          )}
        </div>
      </portals.InPortal>
      {expanded && (
        <div
          ref={refs.setFloating}
          {...getFloatingProps()}
          className={'flex w-fit h-fit z-10 drop-shadow-[0_0_20px_rgba(0,0,0,0.15)]'}>
          <motion.div
            className={`${strategy} p-5 flex flex-col gap-2 mb-5 rounded-lg bg-white w-[200px] transition transition-transform duration-200 ease-in-out`}>
            {choices.map((c) => (
              <div
                key={c.key}
                onClick={(e) => {
                  e.preventDefault()
                  setSortSelected(c.key)
                }}
                className="cursor-pointer">
                <span
                  className={classNames(
                    'text-primary-xiketic font-Spartan text-xs font-bold',
                    { 'opacity-40': c.key !== sortSelected },
                  )}>
                  {c.label}
                </span>
              </div>
            ))}
            <FloatingArrow
              width={24}
              height={12}
              className={`fill-white !left-[66px]`}
              ref={arrowRef}
              context={context}
            />
          </motion.div>
        </div>
      )}
    </>
  )
}

export default SortingController
